import React, { useMemo, useState } from 'react';
import { Box, Flex, Image, Text, VStack } from '@chakra-ui/react';
import type { MarketplaceOpportunityListItemDto } from '@foodfluence/shared';
import { deliverableIsCarousel, deliverableLabel } from '@foodfluence/shared';
import { OpportunityDetailSheet } from '../../collabs/components/OpportunityDetailSheet';
import { MoneyFigure } from '../../../components/ui';
import { ff } from '../../../theme';
import { ImageChip } from './CampaignComponent';

interface Props {
  opportunity: MarketplaceOpportunityListItemDto;
}

const OpportunityCard: React.FC<Props> = ({ opportunity }) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const subtitle = useMemo(() => {
    const projectName = opportunity.orderBrief?.projectName?.trim();
    const narrative = opportunity.orderBrief?.narrative?.trim();
    if (projectName) return projectName;
    if (narrative && narrative.length > 90) return `${narrative.slice(0, 90)}…`;
    return narrative || '';
  }, [opportunity.orderBrief]);

  const deliverableSummary = useMemo(() => {
    if (!opportunity.deliverables) return '';
    return Object.values(opportunity.deliverables)
      .map((d) => {
        const label = deliverableLabel(d.type);
        return deliverableIsCarousel(d.type) && d.count != null ? `${d.count}× ${label}` : label;
      })
      .join(' · ');
  }, [opportunity.deliverables]);

  const displayCash = opportunity.cash ?? 0;

  return (
    <>
      <Box
        as="button"
        type="button"
        onClick={() => setDetailOpen(true)}
        aria-label={`${opportunity.brandName ?? 'Brand'}: Local Creators order`}
        flex="none"
        w="260px"
        display="flex"
        flexDirection="column"
        gap="12px"
        textAlign="left"
        color={ff.ink}
      >
        <Box position="relative" h="160px" w="100%" borderRadius="20px" overflow="hidden" bg={ff.grey}>
          {/* Opportunities carry no brand image yet, so show the brand initial rather than a stock photo. */}
          <Flex h="100%" w="100%" align="center" justify="center">
            <Text textStyle="display" fontSize="35px" color={ff.faint}>
              {(opportunity.brandName ?? '?').charAt(0)}
            </Text>
          </Flex>
          <Box position="absolute" top="12px" left="12px">
            <ImageChip tone="white">Local Creators</ImageChip>
          </Box>
        </Box>
        <Flex align="flex-start" gap="16px" w="100%">
          <VStack flex={1} minW={0} align="stretch" spacing="4px">
            <Text textStyle="display" fontSize="26px" lineHeight="1" letterSpacing="0" fontWeight={600} noOfLines={1}>
              {opportunity.brandName ?? 'Brand'}
            </Text>
            {subtitle && (
              <Text fontSize="15px" lineHeight="1.35" noOfLines={2}>
                {subtitle}
              </Text>
            )}
            {deliverableSummary && (
              <Text fontSize="13px" color={ff.text2} noOfLines={1}>
                {deliverableSummary}
              </Text>
            )}
          </VStack>
          <Box flex="none" pl="16px" borderLeft="1px solid" borderColor={ff.line}>
            <MoneyFigure value={`$${displayCash}`} label="Cash" dim={!displayCash} />
          </Box>
        </Flex>
      </Box>

      <OpportunityDetailSheet
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        opportunity={opportunity}
      />
    </>
  );
};

export default React.memo(OpportunityCard);
